import { useEffect, useState, useRef, type FormEvent } from 'react';
import { api } from '../api';
import { DateOnlyInput } from '../components/DateOnlyInput';
import { alphaSave } from '../ui/alpha-save';
import { ErrorState } from '../ui/State';
export function ConveyancingCompletionAlpha({ caseId, apiRoot }: { caseId: string; apiRoot?: string }) {
  const root = (apiRoot ?? '/api/alpha/conveyancing/' + caseId) + '/completion';
  const [model, setModel] = useState<any>(),
    [primary, setPrimary] = useState(''),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [token, setToken] = useState(''),
    [notice, setNotice] = useState(''),
    pending = useRef<{ path: string; body: object } | null>(null);
  const load = () => api(root).then(setModel);
  useEffect(() => {
    void load().catch(setError);
  }, [caseId]);
  async function save(path: string, body: object) {
    pending.current ??= { path, body: { ...body, idempotencyKey: crypto.randomUUID() } };
    setBusy(true);
    try {
      const result = await alphaSave<any>(pending, root + pending.current.path, {
        method: 'POST',
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      if (result.invitationToken) setToken(result.invitationToken);
      setNotice(result.notice ?? 'Saved. Completion history refreshed.');
      await load();
      setError(undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  function prepare(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget),
      old = model.interests.find((i: any) => i.id === f.get('interest'));
    void save('', {
      interestId: old.id,
      expectedVersion: old.version,
      completionDate: f.get('date'),
      officialOwnerName: f.get('name'),
      personIds: model.incomingOrganisationId ? [] : f.getAll('owners'),
      primaryPersonId: model.incomingOrganisationId ? null : primary,
      organisationId: model.incomingOrganisationId??null,
      addressAssignmentId: f.get('address'),
      evidenceVersionId: f.get('evidence'),
      liabilityReview: f.get('liability'),
    });
  }
  const locked = busy || !!pending.current;
  return (
    <section className="panel">
      <h3>Completion and ownership</h3>
      <p>
        Retain completion evidence, review the effective date, successors and correspondence. A
        second authorised reviewer confirms the change. Account history and balances remain
        unchanged; successor disclosure requires a separate opening-balance review.
      </p>
      {Boolean(error) && <ErrorState error={error} />} {notice && <p role="status">{notice}</p>}
      {token && (
        <label>
          Invitation token — copy for controlled delivery
          <input readOnly value={token} />
        </label>
      )}
      {model && (
        <>
          {model.reviews.map((r: any) => (
            <article key={r.id}>
              <h4>
                {r.payload.officialOwnerName} · {r.status}
              </h4>
              <p>Completed on {r.payload.completionDate}</p>
              <p>{r.payload.liabilityReview}</p>
              {r.status === 'Prepared' && (
                <form
                  className="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const f = new FormData(e.currentTarget);
                    void save('/' + r.id + '/' + f.get('action'), { reason: f.get('reason') });
                  }}
                >
                  <fieldset disabled={locked}>
                    <label>
                      Review outcome
                      <select name="action">
                        <option value="complete">Confirm completion and revoke old access</option>
                        <option value="withdraw">Withdraw preparation</option>
                      </select>
                    </label>
                    <label>
                      Review reason
                      <textarea name="reason" required minLength={10} maxLength={2000} />
                    </label>
                    <button>Save completion review</button>
                  </fieldset>
                </form>
              )}
              {r.status === 'Completed' && !r.payload.organisationId && (
                <form
                  className="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const f = new FormData(e.currentTarget);
                    void save('/' + r.id + '/invitation', {
                      email: f.get('email'),
                      reason: f.get('reason'),
                    });
                  }}
                >
                  <fieldset disabled={locked}>
                    <label>
                      Successor’s verified email
                      <input type="email" name="email" required />
                    </label>
                    <label>
                      Invitation review note
                      <input name="reason" required minLength={10} maxLength={1000} />
                    </label>
                    <button>Prepare invitation without sending</button>
                  </fieldset>
                </form>
              )}
            </article>
          ))}
          {!model.reviews.some((r: any) => r.status !== 'Withdrawn') && (
            <details>
              <summary>Prepare ownership change</summary>
              <form className="form" onSubmit={prepare}>
                <fieldset disabled={locked}>
                  <label>
                    Current legal owner
                    <select name="interest" required>
                      <option value="">Choose current ownership</option>
                      {model.interests.map((i: any) => (
                        <option key={i.id} value={i.id}>
                          {i.official_owner_name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Completion date
                    <DateOnlyInput name="date" required />
                  </label>
                  <label>
                    Successors’ exact legal name
                    <input name="name" required maxLength={500} />
                  </label>
                  {model.incomingOrganisationId ? <p>Purchasing organisation: {model.organisations.find((o:any)=>o.id===model.incomingOrganisationId)?.legal_name}</p> : <><fieldset>
                    <legend>Verified purchaser contacts</legend>
                    {model.people.map((p: any) => (
                      <label key={p.id}>
                        <input name="owners" value={p.id} type="checkbox" />
                        {p.display_name}
                      </label>
                    ))}
                  </fieldset>
                  <label>
                    Correspondence contact
                    <select required value={primary} onChange={(e) => setPrimary(e.target.value)}>
                      <option value="">Choose successor</option>
                      {model.people.map((p: any) => (
                        <option key={p.id} value={p.id}>
                          {p.display_name}
                        </option>
                      ))}
                    </select>
                  </label>
                  </>}<label>
                    Verified correspondence address
                    <select name="address" required>
                      <option value="">Choose retained address</option>
                      {model.addresses
                        .filter((a: any) => model.incomingOrganisationId ? a.organisation_id===model.incomingOrganisationId : a.person_id === primary)
                        .map((a: any) => (
                          <option key={a.id} value={a.id}>
                            {a.line_1}, {a.town_city}, {a.postcode}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label>
                    Completion evidence
                    <select name="evidence" required>
                      <option value="">Choose reviewed evidence</option>
                      {model.documents.map((d: any) => (
                        <option key={d.id} value={d.id}>
                          {d.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Liability and account-history review
                    <textarea name="liability" required minLength={10} maxLength={4000} />
                  </label>
                  <button>Prepare for separate review</button>
                </fieldset>
              </form>
            </details>
          )}
          <h4>Retained completion history</h4>
          {model.history.map((h: any) => (
            <p key={h.id}>
              {h.event_type} · {h.created_at} {h.detail.delivery && ' · ' + h.detail.delivery}
            </p>
          ))}
        </>
      )}
      {pending.current && (
        <button
          disabled={busy}
          onClick={() => void save(pending.current!.path, pending.current!.body)}
        >
          Retry the same completion action
        </button>
      )}
    </section>
  );
}
export function AcceptSuccessorInvitation({ onAccepted }: { onAccepted: () => void }) {
  const [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [done, setDone] = useState(false),
    pending = useRef<object | null>(null);
  return (
    <details>
      <summary>Accept a property invitation</summary>
      {Boolean(error) && <ErrorState error={error} />}{' '}
      {done ? (
        <p role="status">Invitation accepted. Your available homes have been refreshed.</p>
      ) : (
        <form
          className="form"
          onSubmit={async (e) => {
            e.preventDefault();
            pending.current ??= {
              token: new FormData(e.currentTarget).get('token'),
              idempotencyKey: crypto.randomUUID(),
            };
            setBusy(true);
            try {
              await alphaSave(pending, '/api/portal/leaseholder/invitations/accept', {
                method: 'POST',
                body: JSON.stringify(pending.current),
              });
              pending.current = null;
              setDone(true);
              setError(undefined);
              onAccepted();
            } catch (e) {
              setError(e);
            } finally {
              setBusy(false);
            }
          }}
        >
          <label>
            Invitation token
            <input
              name="token"
              required
              minLength={30}
              maxLength={100}
              disabled={busy || !!pending.current}
            />
          </label>
          <p>Use the account with the email address named in your invitation.</p>
          <button disabled={busy}>
            {pending.current ? 'Retry acceptance' : 'Accept invitation'}
          </button>
        </form>
      )}
    </details>
  );
}
